<template>
  <article
    class="keyboard"
    @pointerdown="onPointerDown"
    @pointercancel="onPointerCancel"
    @touchstart="onPointerDown"
    @touchcancel="onPointerCancel"
  >
    <div class="keyboard-actions">
      <button class="close-button" @click="hideKeyboard">
        {{ $t('components.keyboard.close-button') }}
      </button>
    </div>
    <div class="keyboard-keys">
      <button
        v-for="key in keys"
        :key="key.label"
        class="keyboard-key"
        :class="key.method"
        type="button"
        @click="onKeyPress(key)"
      >
        <FaIcon v-if="key.icon" :icon="key.icon" />{{ key.label }}
      </button>
    </div>
  </article>
</template>

<script>
import FaIcon from '../ui/FaIcon'
import { KeyboardStoreNamespacedTypes } from '../../store/keyboard'

/**
 * Enum for determining the action performed when a key is pressed on the software keyboard.
 * @enum {string}
 */
export const KeyboardMethods = {
  BACKSPACE: 'backspace',
  INJECT: 'inject',
}

/**
 * @typedef {{icon: string|undefined, label: string|undefined, method: KeyboardMethods: value: string|undefined}} KeyConfig
 */

/**
 * A software keyboard for entering values into custom input fields.
 * The keyboard supports decimal numbers both positive and negative.
 *
 * This component only works together with the keyboard store.
 *
 * For a custom input field to be compatible, it should be a Vue component and expose the methods `getValue():string` and `setValue(string)`.
 *
 * To attach the keyboard to an input field, the input field should call `KeyboardStoreNamespacedTypes.actions.SET_FOCUS` with `this` as payload.
 *
 * Usage: <Keyboard />
 *
 * @author Thomas Grinderslev <lritgr@egmont.com>
 */
export default {
  name: 'Keyboard',
  components: { FaIcon },
  data() {
    /**
     * An array of value keys.
     * When a value key is pressed its value will be appended to the value of the focused input field.
     * @type {Array<string>}
     */
    const valueKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', ',']

    return {
      /**
       * @type {Array<KeyConfig>}
       */
      keys: [
        // Add all of the value keys
        ...valueKeys.map(value => ({
          label: value,
          method: KeyboardMethods.INJECT,
          value,
        })),
        // Add a minus key (using an em dash for the label making it more visible than a standard minus/dash)
        { label: '—', method: KeyboardMethods.INJECT, value: '-' },
        // Add a backspace key
        { icon: 'backspace', method: KeyboardMethods.BACKSPACE },
      ],
    }
  },
  computed: {
    /**
     * Returns the currently focused input field according to the keyboard store.
     * @return {Vue}
     */
    currentInputField() {
      return this.$store.getters[
        KeyboardStoreNamespacedTypes.getters.GET_FOCUSED_INPUT_FIELD
      ]()
    },
  },
  mounted() {
    // Make sure the current input field is still visible in the viewport after the keyboard has been rendered
    if (this.currentInputField) {
      this.$nextTick(() => {
        this.currentInputField.$el.scrollIntoView()
      })
    }
  },
  methods: {
    /**
     * Returns true if the given input field component is supported.
     * @param {Vue} inputField
     * @returns {boolean}
     */
    isInputFieldSupported(inputField) {
      return (
        inputField &&
        inputField.removeCharacter &&
        inputField.addCharacter &&
        inputField.focus
      )
    },

    /**
     * Tells the store that a keypress is in progress.
     */
    onPointerDown() {
      this.$store.dispatch(
        KeyboardStoreNamespacedTypes.actions.SET_KEYPRESS_IN_PROGRESS,
        true
      )
    },

    /**
     * Tells the store that a keypress is no longer in progress.
     */
    onPointerCancel() {
      this.$store.dispatch(
        KeyboardStoreNamespacedTypes.actions.SET_KEYPRESS_IN_PROGRESS,
        false
      )
    },

    /**
     * Callback function for all keyboard presses.
     * Sends the given key to the current input field and resets the keypress progress state in the keyboard store.
     * @param {KeyConfig} key
     */
    onKeyPress(key) {
      // Ignore all key presses if there is no current input field or it doesn't support the required methods
      if (!this.isInputFieldSupported(this.currentInputField)) {
        return
      }

      switch (key.method) {
        case KeyboardMethods.BACKSPACE:
          this.currentInputField.removeCharacter()
          break
        case KeyboardMethods.INJECT:
          this.currentInputField.addCharacter(key.value)
          break
      }

      this.$store.dispatch(
        KeyboardStoreNamespacedTypes.actions.SET_KEYPRESS_IN_PROGRESS,
        false
      )

      this.currentInputField.focus()
    },

    /**
     * Hides the keyboard by clearing focus in the keyboard store.
     */
    hideKeyboard() {
      this.$store.dispatch(KeyboardStoreNamespacedTypes.actions.CLEAR_FOCUS)
    },
  },
}
</script>

<style lang="scss" scoped>
.keyboard {
  --keyboard-margin: 1.5rem;
  --keyboard-text-color: var(--blue);
  --keyboard-background: #{mix($mainColor, $white, 60%)};

  position: fixed;
  bottom: var(--keyboard-margin);
  left: var(--keyboard-margin);
  right: var(--keyboard-margin);
  margin: 0 auto 1.5rem;
  font-size: 1rem;
  background: var(--keyboard-background);
  border-radius: var(--border-radius-large);
  box-shadow: var(--content-holder-box-shadow);
  z-index: 20000;
  overflow: hidden;
  text-align: center;
  touch-action: manipulation; // Prevents double tap zooming when typing quickly
  user-select: none; // Prevents selection of text in the keyboard when using it
}

.keyboard-actions {
  text-align: right;
}

.close-button {
  background: none;
  border: none;
  box-shadow: none;
  color: $white !important;
  padding: 0.5em;
  margin-right: 0.75em;

  &:focus {
    outline: none;
  }
}

.keyboard-keys {
  display: grid;
  grid-gap: 1px;
}

.keyboard-key {
  color: var(--blue) !important;
  background: none;
  border: none;
  border-radius: 0;
  padding: var(--content-padding-large);
  background: var(--white) !important;

  &:focus {
    box-shadow: none;
    outline: none;
  }
  &:active {
    background: var(--off-white);
  }
}

/*
 * Display the keyboard in a grid and let it touch the edges of the screen on narrow screens
 */
@media (max-width: 38.75rem) {
  .keyboard {
    --keyboard-margin: 0;

    max-width: none;
    border-radius: 0;
    margin-bottom: 0;
  }
  .keyboard-keys {
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: repeat(4, 1fr);
  }
  .keyboard-key {
    grid-column: span 4;

    &:nth-last-child(-n + 4) {
      grid-column: span 3;
    }
  }
}

/*
 * Display the keyboard in a row on screens wide enough to contain it
 */
@media (min-width: 38.76rem) {
  .keyboard {
    --keyboard-margin: 2rem;
    border-radius: var(--theme-border-radius, var(--border-radius-medium));
  }
  .keyboard-keys {
    grid-template-columns: repeat(13, 1fr);
  }
}
</style>
